import { useEffect, useState, useRef, useCallback } from 'react';
import { filterAlarms, getFireAlarms, sortAlarmsBySeverity } from './utils';
import Raven from 'raven-js';
const useGetFireAlarms = () => {
  // Store all potentially relevant alarms after initial filtering (URL, debug, etc.)
  const [potentialAlarms, setPotentialAlarms] = useState([]);
  // Store alarms currently visible based on querySelector
  const [visibleAlarms, setVisibleAlarms] = useState([]);
  // Ref to store observer for cleanup
  const observerRef = useRef(null);

  // Function to check selectors and update visible alarms
  const checkSelectors = useCallback(() => {
    const newlyVisibleAlarms = potentialAlarms.filter(alarm => {
      if (!alarm.querySelector) {
        // If no selector, it's always visible (if it passed initial filters)
        return true;
      }
      try {
        // Determine where to search - current document or parent if in iframe
        const isInIframe = window !== window.top;
        const docToSearch = isInIframe && window.parent ? window.parent.document : window.document;

        // Check if the selector matches any elements
        const matches = docToSearch.querySelectorAll(alarm.querySelector);
        if (matches.length > 0) {
          return true;
        }
        return false;
      } catch (e) {
        // Invalid selector syntax, treat as not found
        console.warn(`Invalid selector for fire alarm ${alarm.id}: ${alarm.querySelector}`);
        return false;
      }
    });

    // Update state only if the visible alarms have actually changed
    setVisibleAlarms(currentVisible => {
      const currentIds = new Set(currentVisible.map(a => a.id));
      const newIds = new Set(newlyVisibleAlarms.map(a => a.id));

      // Return same reference if no changes (prevents unnecessary re-renders)
      if (currentIds.size === newIds.size && [...currentIds].every(id => newIds.has(id))) {
        return currentVisible;
      }
      return newlyVisibleAlarms;
    });
  }, [potentialAlarms]);

  // Fetch initial alarms
  useEffect(() => {
    let isMounted = true;
    getFireAlarms().then(res => {
      if (!isMounted) return;
      const filteredFireAlarms = filterAlarms(res);
      const sortedFireAlarms = sortAlarmsBySeverity(filteredFireAlarms);
      setPotentialAlarms(sortedFireAlarms);
    }).catch(e => {
      if (!isMounted) return;
      Raven.captureException(e);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  // Effect to monitor DOM changes for selectors
  useEffect(() => {
    // Perform an initial check for elements as soon as we load
    if (potentialAlarms.length > 0) {
      // Give DOM time to be fully ready
      setTimeout(checkSelectors, 300);
    }
    if (potentialAlarms.length === 0) {
      var _observerRef$current;
      setVisibleAlarms(potentialAlarms);
      (_observerRef$current = observerRef.current) === null || _observerRef$current === void 0 || _observerRef$current.disconnect();
      observerRef.current = null;
      return;
    }

    // Create MutationObserver to watch for DOM changes
    const observerCallback = () => {
      // Use requestAnimationFrame to run after browser paint cycle
      requestAnimationFrame(checkSelectors);
    };

    // Determine what to observe based on iframe context
    const isInIframe = window !== window.top;
    let targetToObserve;
    try {
      // If in iframe, try to observe parent document
      if (isInIframe && window.parent && window.parent.document.body) {
        targetToObserve = window.parent.document.body;
      } else {
        targetToObserve = window.document.body;
      }

      // Create and attach the observer
      const observer = new MutationObserver(observerCallback);
      observer.observe(targetToObserve, {
        childList: true,
        subtree: true
      });
      observerRef.current = observer;
    } catch (error) {
      console.error('Error setting up MutationObserver:', error);
    }

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [potentialAlarms, checkSelectors]);

  // Return the dynamically updated list of visible alarms
  return visibleAlarms;
};
export default useGetFireAlarms;